import axios from "axios";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { API, DiscountTypes } from "../../../helpers/constants";
import { handleAxiosErrors, logoutUnauthorizedUser } from "../../../helpers/errorHandling";
import useUserAction from "../../../hooks/user/useUserAction";
import useUserData from "../../../hooks/user/useUserData";
import { v4 as uuidv4 } from "uuid";

const mapItems = (items) => {
	return items.map(item => ({
		id: item.id ?  item.id : uuidv4(),
		ItemId: item.ItemId && item.Description ? { id: item.ItemId, Description: item.Description } : '',
		ItemDescription: item.ItemDescription || '',
		Qty: item?.Qty ? item.Qty : 0,
		ItemPrice: item?.itemValue ? parseFloat(item.itemValue) : 0,
		ItemValue: item?.Qty * parseFloat(item?.itemValue),
		ChargeDescription: item?.ChargeDescription || '',
		PCTCharge: item?.PCTCharge ? parseFloat(item.PCTCharge) : 0,
		Note: item?.Note || '',
	}))
}

const mapCharges = (charges) => {
	return charges.map(charge => ({
		id: uuidv4(),
		ChargeDescription: charge.ChargeDescription || '',
		PCTCharge: charge?.PCTCharge ? parseFloat(charge.PCTCharge) : 0,
		Note: charge?.Note || '',
	}))
}

const mapAttachments = (files) => {
	return files.map(file => ({
		id: file.id,
		name: file.oldFiles,
		sendDate: file.SendDt,
		recipients: file.Recipients,
		status: file.Replied === '1' ? 'Approved' : file.Replied === '0' ? 'Rejected' : 'Pending',
		actionDate: file.ActionDt,
		repliedBy: file.RepliedBy
	}))
}

const useCustomerSO = ({ issueId }) => {
	const { t } = useTranslation()
	const { token, username, userId, isLoggedIn, dateFormat, timeFormat } = useUserData()
	const { userLogout } = useUserAction()
	const [data, setData] = useState({
		soId: null,
		markup: 0,
		discount: 0,
		discountType: DiscountTypes.Percentage,
		comment: '',
		charges: [],
		items: [],
		previousOffers: []
	})
	const [items, setItems] = useState([])
	const [loading, setLoading] = useState(true)
	
	const dataManager = useCallback(() => {
		axios({
			method: 'post',
			url: `${API}/en/custom/workOrder`,
			data: {
				IssueId: issueId
			},
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
		}).then((response) => {
			const { workOrder, details, charges, items, attachments } = response.data
			setData(prev => ({
					...prev,
					...(workOrder && workOrder.id ? { soId: workOrder.id } : {}),
					...(workOrder && workOrder.MarkUp ? { markup: Number(workOrder.MarkUp) } : {}),
					...(workOrder && workOrder.Discount ? { discount: parseFloat(workOrder.Discount) } : {}),
					...(workOrder && workOrder.isPCT ? { discountType: workOrder.isPCT } : {}),
					...(workOrder && workOrder.Comments ? { comment: workOrder.Comments } : {}),
					...(charges && charges.length > 0 ? { charges: [...mapCharges(charges)] } : {}),
					...(details && details.length > 0 ? { items: [...mapItems(details)] } : {}),
					...(attachments && attachments.length > 0 ? { previousOffers: [...mapAttachments(attachments)] } : {}),
				})
			)
			setItems(items)
			setLoading(false)
		}).catch(error => {
			enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
				variant: 'error',
				autoHideDuration: 3000,
			})
			setLoading(false)
			logoutUnauthorizedUser(error, isLoggedIn, userLogout)
		})
	}, [t, isLoggedIn, userLogout, issueId])
	
	useEffect(() => {
		dataManager()
	}, [logoutUnauthorizedUser, token, isLoggedIn, userLogout, t]);
	
	return {
		loading,
		data,
		items,
		soDataManager: dataManager,
	}
}

export default useCustomerSO